import * as Config from '../config';
import MAPNOTES from '../../../mock-data/mapNotelets.json';
import FULLMAPNOTES from '../../../mock-data/mapNoteFull.json';

/** Maps the values of a plain object, returning a new object with the same keys. */
const mapRecord = (obj, fn) => Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, fn(value, key)]));

let storedMapNotesFull = FULLMAPNOTES;
let storedMapNotelets = MAPNOTES;
let storedNextMapNoteId = 1;

const getMapNoteById = noteId => {
    const mapNote = storedMapNotesFull.find(note => note.id === noteId);
    return mapNote;
};

const getMapNoteletById = noteId => {
    const mapNotelet = storedMapNotelets.find(note => note.id === noteId);
    return mapNotelet;
};

const mockMapNotelets = async () => {
    storedMapNotelets = MAPNOTES;
    storedMapNotesFull = FULLMAPNOTES;
    return MAPNOTES;
};

const mockMapNote = async noteId => getMapNoteById(noteId);

const mockUpdateMapNote = async (noteId, changes) => {
    // MapNote/mapNotelets that need to change
    const mapNoteToChange = getMapNoteById(noteId);
    const mapNoteletToChange = getMapNoteletById(noteId);
    const addHistoryComment = (key, value) => ({
        newValue: value,
        previousValue: null,
        createdAt: new Date().toISOString(),
        fieldName: key,
        type: 'history',
    });

    const changedFields = Object.keys(changes);
    const changeFieldValues = obj =>
        mapRecord(obj, (value, key) => (changedFields.includes(key) ? changes[key] : value));

    // Indices to change
    const mapNoteletIndexToChange = storedMapNotelets.findIndex(n => n.id === noteId);
    const mapNoteIndexToChange = storedMapNotesFull.findIndex(n => n.id === noteId);

    // New mapNote/mapNotelets resulted from the change
    const newMapNotelet = changeFieldValues(mapNoteletToChange);
    const newMapNote = changeFieldValues(mapNoteToChange);

    const lastKey = changedFields[changedFields.length - 1];
    const historyComment = addHistoryComment(lastKey, changes[lastKey]);
    const newMapNoteWithHistory = {
        ...newMapNote,
        comments: [...(newMapNote.comments || []), historyComment],
    };

    storedMapNotelets[mapNoteletIndexToChange] = newMapNotelet;
    storedMapNotesFull[mapNoteIndexToChange] = newMapNoteWithHistory;

    return newMapNoteWithHistory;
};

const mockAddMapNoteComment = async (email, noteId, comment) => {
    const mapNote = getMapNoteById(noteId);
    if (!mapNote) return null;
    const mapNoteIndexToChange = storedMapNotesFull.findIndex(n => n.id === noteId);
    const oldComments = mapNote.comments || [];

    if (comment && Object.prototype.hasOwnProperty.call(comment, 'comment')) {
        const newComment = {
            body: comment.comment,
            createdAt: new Date().toISOString(),
            createdBy: { email },
            id: storedNextMapNoteId++,
            type: 'comment',
            uploads: [],
        };

        const newMapNote = {
            ...mapNote,
            comments: [...oldComments, newComment],
        };
        storedMapNotesFull[mapNoteIndexToChange] = newMapNote;

        return newMapNote;
    }
    const newComment = {
        body: '',
        createdAt: new Date().toISOString(),
        createdBy: { email },
        id: storedNextMapNoteId++,
        type: 'comment',
        uploads: [
            'https://cdn2-www.dogtime.com/assets/uploads/gallery/golden-retriever-dogs-and-puppies/golden-retriever-dogs-puppies-9.jpg',
        ],
    };

    const newMapNote = {
        ...mapNote,
        comments: [...oldComments, newComment],
    };
    storedMapNotesFull[mapNoteIndexToChange] = newMapNote;

    return newMapNote;
};

const createdMapNoteFull = (newNote, email) => ({
    id: storedNextMapNoteId,
    url: `https://locusmapsrequests.zendesk.com/api/v2/tickets/${storedNextMapNoteId}.json`,
    noteStatus: 'open',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    subject: newNote.subject,
    description: newNote.description,
    noteType: newNote.noteType || 'noType',
    location: {
        venueId: newNote.location.venueId,
        ordinalId: newNote.location.ordinalId,
        latitude: newNote.location.latitude,
        longitude: newNote.location.longitude,
        geohash: newNote.location.geohash,
        structureId: newNote.location.structureId,
        levelId: newNote.location.levelId,
    },
    accountIds: [Config.getAccountId()],
    createdBy: { email },
    comments: [],
    dueAt: new Date().toISOString(),
    isPublic: true,
});

const createdMapNotelet = newNote => ({
    id: storedNextMapNoteId,
    createdAt: new Date().toISOString(),
    subject: newNote.subject,
    description: newNote.description,
    location: {
        venueId: newNote.location.venueId,
        ordinalId: newNote.location.ordinalId,
        latitude: newNote.location.latitude,
        longitude: newNote.location.longitude,
    },
    noteStatus: 'open',
    noteType: newNote.noteType || 'noType',
    accountIds: [Config.getAccountId()],
});

const mockCreateMapNote = async (newNote, email) => {
    const newCreatedMapNoteFull = createdMapNoteFull(newNote, email);
    const newCreatedMapNotelet = createdMapNotelet(newNote);

    storedMapNotelets = [newCreatedMapNotelet, ...storedMapNotelets];
    storedMapNotesFull = [newCreatedMapNoteFull, ...storedMapNotesFull];
    storedNextMapNoteId = ++storedNextMapNoteId;

    return newCreatedMapNoteFull;
};

const mockDeleteMapNote = async noteId => {
    const noteletIndex = storedMapNotelets.findIndex(n => n.id === noteId);
    if (noteletIndex > -1) {
        storedMapNotelets = [...storedMapNotelets.slice(0, noteletIndex), ...storedMapNotelets.slice(noteletIndex + 1)];
    }
    const noteIndex = storedMapNotesFull.findIndex(n => n.id === noteId);
    if (noteIndex > -1) {
        storedMapNotesFull = [...storedMapNotesFull.slice(0, noteIndex), ...storedMapNotesFull.slice(noteIndex + 1)];
    }
};

const mockDeleteMapNoteComment = async (noteId, commentId) => {
    const mapNote = getMapNoteById(noteId);
    if (!mapNote) return { ok: false };

    const newCommentsAfterDelete = (mapNote.comments || []).filter(c => c.id !== commentId);
    const newMapNote = {
        ...mapNote,
        comments: newCommentsAfterDelete,
    };
    const noteIndex = storedMapNotesFull.findIndex(n => n.id === noteId);
    if (noteIndex > -1) {
        storedMapNotesFull[noteIndex] = newMapNote;
    }
    return { ok: true };
};

const MockMapNotes = {
    getMapNotelets: () => mockMapNotelets(),
    getMapNoteTotals: () => Promise.resolve({ open: 4, resolved: 2 }),
    getSelectedMapNote: (venueId, noteId) => mockMapNote(noteId),
    deleteMapNote: (venueId, noteId) => mockDeleteMapNote(noteId),
    updateMapNote: (venueId, noteId, changes) => mockUpdateMapNote(noteId, changes),
    addMapNoteComment: (venueId, email, noteId, comment) => mockAddMapNoteComment(email, noteId, comment),
    createMapNote: (venueId, email, newNote) => mockCreateMapNote(newNote, email),
    deleteMapNoteComment: (venueId, email, noteId, commentId) => mockDeleteMapNoteComment(noteId, commentId),

    // Testing functions
    getMapNoteById,
    getMapNoteletById,
};

export { MockMapNotes };
